/**
 * Gold "never used" dots (#1838) — office only, never on the tech iPad.
 *
 * Mark a button with `data-teach="<key>"` (the key must be in features.ts —
 * CI checks). Mount <TeachDots /> on a screen that should teach its buttons.
 * The layer looks for marked buttons on screen, asks rules.dotsFor which (at
 * most 3, role order) this person has never used, and draws a small pulsing
 * gold dot on each. Tap the dot: a tip with a picture and one line, Not now
 * (14 days) or Try it (counts as using it, and presses the button).
 *
 * Dots float in a portal at the button's corner, so no screen's layout
 * changes to carry them.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { openCommandPalette } from "@/lib/job-actions-bus";
import { cn } from "@/lib/utils";
import { feature } from "./features";
import { TeachPicture } from "./pictures";
import { dotsFor } from "./rules";
import { useTeach } from "./teach-state";

interface Placed {
    key: string;
    top: number;
    left: number;
}

function visible(el: Element): DOMRect | null {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) return null;
    return r;
}

/** Every marked button visible right now, first one per key. */
export function findTeachTargets(root: ParentNode = document): Map<string, Element> {
    const out = new Map<string, Element>();
    root.querySelectorAll("[data-teach]").forEach((el) => {
        const key = el.getAttribute("data-teach");
        if (key && !out.has(key) && visible(el)) out.set(key, el);
    });
    return out;
}

/**
 * `only` limits the dots to these keys — a screen that wants exactly one
 * teaching item (the Tasks Team tab: `tasks.take`) mounts it this way, so the
 * top bar's own dots don't pile on.
 */
export function TeachDots({ only }: { only?: string[] } = {}) {
    const t = useTeach();
    const [placed, setPlaced] = useState<Placed[]>([]);
    const [open, setOpen] = useState<string | null>(null);
    const blocked = !t.state.on || t.role !== "office" || t.state.tipsOff || t.active !== null;

    const measure = useCallback(() => {
        if (blocked) {
            setPlaced((p) => (p.length ? [] : p));
            return;
        }
        const targets = findTeachTargets();
        const keys = dotsFor(targets.keys(), t.state, t.role, new Date()).filter((k) => !only || only.includes(k));
        const next: Placed[] = [];
        for (const key of keys) {
            const r = targets.get(key)?.getBoundingClientRect();
            if (r) next.push({ key, top: r.top - 6, left: r.right - 10 });
        }
        setPlaced((prev) =>
            prev.length === next.length &&
            prev.every((p, i) => p.key === next[i].key && p.top === next[i].top && p.left === next[i].left)
                ? prev
                : next,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocked, t.state, t.role, only?.join("|")]);

    useEffect(() => {
        measure();
        // Screens load, scroll and re-render under us; a light poll keeps the
        // dots on their buttons without hooking every screen's state.
        const id = window.setInterval(measure, 700);
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.clearInterval(id);
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [measure]);

    // A dot that disappeared (used, snoozed) takes its tip with it.
    useEffect(() => {
        if (open && !placed.some((p) => p.key === open)) setOpen(null);
    }, [open, placed]);

    if (placed.length === 0) return null;
    return createPortal(
        <>
            {placed.map((p) => (
                <button
                    key={p.key}
                    type="button"
                    aria-label={`New to you: ${feature(p.key)?.name ?? p.key}`}
                    aria-expanded={open === p.key}
                    data-testid={`teach-dot-${p.key}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen((o) => (o === p.key ? null : p.key));
                    }}
                    className="group fixed z-[80] grid h-4 w-4 place-items-center rounded-full bg-primary p-0 shadow ring-2 ring-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    style={{ top: p.top, left: p.left }}
                >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                    <span className="pointer-events-none absolute -inset-1.5 rounded-full ring-2 ring-primary motion-safe:animate-ping" />
                </button>
            ))}
            {open && (
                <TeachTip
                    tipKey={open}
                    anchor={placed.find((p) => p.key === open)!}
                    onClose={() => setOpen(null)}
                    onNotNow={() => {
                        t.snooze(open);
                        setOpen(null);
                    }}
                    onTry={() => {
                        const key = open;
                        setOpen(null);
                        t.markUsed(key);
                        if (feature(key)?.tip?.tryIt === "palette") {
                            openCommandPalette();
                            return;
                        }
                        const el = findTeachTargets().get(key) as HTMLElement | undefined;
                        el?.click();
                    }}
                    onTipsOff={() => {
                        setOpen(null);
                        t.setTipsOff(true);
                        toast("Tips are off for you. Turn them back on in What's new.");
                    }}
                />
            )}
        </>,
        document.body,
    );
}

function TeachTip({
    tipKey,
    anchor,
    onClose,
    onNotNow,
    onTry,
    onTipsOff,
}: {
    tipKey: string;
    anchor: Placed;
    onClose: () => void;
    onNotNow: () => void;
    onTry: () => void;
    onTipsOff: () => void;
}) {
    const f = feature(tipKey);
    const tryRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        tryRef.current?.focus();
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        const onDown = (e: MouseEvent) => {
            const el = e.target as Element | null;
            if (!el?.closest?.("[data-teach-tip]") && !el?.closest?.("[data-testid^='teach-dot-']")) onClose();
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
        };
    }, [onClose]);
    if (!f?.tip) return null;
    const width = Math.min(290, window.innerWidth - 24);
    const left = Math.max(12, Math.min(anchor.left - width / 2, window.innerWidth - width - 12));
    const below = anchor.top + 26;
    const top = below + 240 > window.innerHeight ? Math.max(12, anchor.top - 250) : below;
    return (
        <div
            role="dialog"
            aria-label={f.name}
            data-teach-tip
            data-testid={`teach-tip-${tipKey}`}
            className="fixed z-[85] grid gap-2 rounded-xl bg-card p-3 ring-1 ring-primary text-left text-card-foreground shadow-xl"
            style={{ top, left, width }}
        >
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-primary">
                New to you
            </span>
            <h4 className="m-0 text-base font-bold">{f.name}</h4>
            <TeachPicture pic={f.tip.pic} className="max-h-48" />
            <p className="m-0 text-sm">{f.tip.line}</p>
            <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onTipsOff}
                    className="mr-auto text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                    Don&apos;t show tips
                </button>
                <button
                    type="button"
                    onClick={onNotNow}
                    className="rounded-md px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted"
                >
                    Not now
                </button>
                <button
                    ref={tryRef}
                    type="button"
                    onClick={onTry}
                    className={cn(
                        "rounded-md bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90",
                    )}
                >
                    Try it
                </button>
            </div>
        </div>
    );
}
